import React, { useState } from 'react';
import { Form, Input, Button, Typography, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { setVerificationToken } from '../../store/slices/authSlice';
import { forgotPassword } from '../../services/authService';
import AuthPageLayout from '../../components/auth/AuthPageLayout';
import AuthFormContainer, { Header } from '../../components/auth/AuthFormContainer';
import EmailSentModal from '../../components/auth/EmailSentModal';
import ButtonGroup from '../../components/common/ButtonGroup';

const { Title, Text } = Typography;

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const handleFormChange = () => {
    const values = form.getFieldsValue(['email']);
    const email = values.email?.trim() || '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailValid = emailRegex.test(email);
    setIsFormValid(isEmailValid);
  };

  const handleSubmit = async (values: ForgotPasswordFormData) => {
    try {
      setIsSubmitting(true);
      const response = await forgotPassword({ email: values.email });
      // Save verificationToken to Redux
      dispatch(setVerificationToken(response.data.verificationToken));
      // Show success modal
      setShowModal(true);
      // Navigate to OTP page after modal shows
      setTimeout(() => {
        setIsSubmitting(false);
        setShowModal(false);
        navigate('/auth/verify-otp', { state: { email: values.email } });
      }, 2000);
    } catch (error: any) {
      // Case 1: Validation error - show inline error message (e.g., email not found)
      if (error?.code === 'VALIDATION_ERROR' && error?.data) {
        const fieldErrors: any[] = [];
        if (error.data.email && Array.isArray(error.data.email)) {
          fieldErrors.push({
            name: 'email',
            errors: error.data.email,
          });
        }
        if (fieldErrors.length > 0) {
          form.setFields(fieldErrors);
          return;
        }
      }
      // Case 2: Other errors - show toast directly
      message.error(error?.message || 'Failed to send reset code. Please try again.');
      console.error('Forgot password failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageLayout>
      <AuthFormContainer>
        <Header>
          <Title level={2}>Forgot your password?</Title>
          <Text className="secondaryText">
            Enter your email address and we&apos;ll send you the code to reset your password.
          </Text>
        </Header>

        <Form
          form={form}
          name='forgotpassword'
          onFinish={handleSubmit}
          onValuesChange={handleFormChange}
          layout='vertical'
          autoComplete='off'
          requiredMark={false}
        >
          <Form.Item
            name='email'
            label='Email address'
            style={{ marginBottom: 32 }}
            rules={[
              {
                required: true,
                message: 'Please input your email!',
              },
              {
                type: 'email',
                message: 'Please enter a valid email!',
              },
            ]}
          >
            <Input
              placeholder='name@company.com'
              autoComplete='email'
            />
          </Form.Item>
        </Form>

        <ButtonGroup>
          <Button
            type='default'
            size='large'
            onClick={() => navigate('/auth/signin')}
          >
            Go back
          </Button>

          <Button
            type='primary'
            htmlType='submit'
            loading={isSubmitting}
            size='large'
            form='forgotpassword'
            disabled={!isFormValid}
            >
            {isSubmitting ? 'Sending...' : 'Send reset code'}
          </Button>
        </ButtonGroup>

        <EmailSentModal
          open={showModal}
          onClose={() => setShowModal(false)}
        />
      </AuthFormContainer>
    </AuthPageLayout>
  );
};

export default ForgotPasswordPage; 